import { Helmet } from 'react-helmet';
import { sendPartnerInvite } from '../../../fetch/fetchUsers';
import { UsersMenu } from '../UsersMenu';
import React from 'react';
import { ButtonComponent as Button } from '../../../ReusableComponents/Button/ButtonComponent';
import * as Sentry from '@sentry/react';

import './InviteUser.scss';

type InviteEventState = 'pending' | 'success' | 'failure';
type InviteEventFinalState = Extract<InviteEventState, 'success' | 'failure'>;
export type InviteEvent = { to: string; state: InviteEventState };

type Props = {
  inviteLog: InviteEvent[];
  sendInvite: (to: string) => Promise<InviteEventFinalState>;
};

export const Content: React.FC<Props> = ({ inviteLog, sendInvite }) => {
  const inputName = 'email-input';
  const formId = 'user-invite-form';

  const StatusMessage: React.FC<{ invite: InviteEvent }> = ({ invite }) => {
    switch (invite.state) {
      case 'success':
        return (
          <span data-state={invite.state}>
            The invite for {invite.to} was sent successfully.
          </span>
        );
      case 'failure':
        return (
          <span data-state={invite.state}>
            The invite for {invite.to} failed to send.
            <Button
              variant="text"
              size="small"
              data-variant="text"
              onClick={() => sendInvite(invite.to)}
            >
              Retry
            </Button>
          </span>
        );
      case 'pending':
        return (
          <span data-state={invite.state}>
            Hang on! We're still working on the invite for {invite.to}.
          </span>
        );
    }
  };

  return (
    <main className="invite-user-container">
      <section className="main-content">
        <h1 className="h1">Invite users</h1>
        <p>
          To invite new partners to the system, use the form below. Enter the
          email address you want to send the invite to and submit the form by
          pressing the button or the enter key. You can send as many invites as
          you want to.
        </p>
        <form
          id={formId}
          onSubmit={e => {
            e.preventDefault();
            const form = document.querySelector(
              `#${formId}`
            ) as HTMLFormElement;

            const input = form.querySelector(
              `input[name=${inputName}]`
            ) as HTMLInputElement;
            const to = input.value.trim();

            sendInvite(to);
            form.reset();
          }}
        >
          <div>
            <label htmlFor={inputName}>Email address</label>

            <input
              id={inputName}
              name={inputName}
              type="email"
              required
              placeholder="you@example.com"
            />
          </div>
          <Button data-variant="contained" type="submit">
            Send invite
          </Button>
        </form>
        <section className="invite-log">
          <h2>Invite log</h2>
          <p>
            Invites you send in this session (until you close the window or
            refresh the page) are listed below along with their current status.
          </p>
          <ol aria-live="polite">
            {inviteLog.map((x, i) => (
              <li key={i} data-state={x.state}>
                <StatusMessage invite={x} />
              </li>
            ))}
          </ol>
        </section>
      </section>
    </main>
  );
};

type WrappedContentProps = {
  sendInvite: (to: string) => Promise<any>;
};
export const ContentWrapped: React.FC<WrappedContentProps> = ({
  sendInvite,
}) => {
  const reducer = (state, e) => {
    switch (e.type) {
      case 'add':
        return {
          ...state,
          [e.data.requestTimestamp]: { to: e.data.to, state: 'pending' },
        };
      case 'update':
        return {
          ...state,
          [e.data.requestTimestamp]: { to: e.data.to, state: e.data.state },
        };
    }
  };

  const [invites, dispatch] = React.useReducer(reducer, {});

  const sendInviteWrapped = async (
    to: string
  ): Promise<InviteEventFinalState> => {
    const requestTimestamp = Date.now();

    dispatch({ type: 'add', data: { to, requestTimestamp } });

    const updateEvent = (state: InviteEventFinalState) => () => {
      const data = { to, state, requestTimestamp };
      dispatch({ type: 'update', data });
      return state;
    };

    return sendInvite(to)
      .then(updateEvent('success'))
      .catch(e => {
        console.error(
          `Encountered an error when trying to create an invite for ${to}:`,
          e.message
        );

        Sentry.captureException(e);

        return updateEvent('failure')();
      });
  };

  const inviteLog = Object.entries<InviteEvent>(invites).map(([k, v]) => ({
    timestamp: Number(k),
    to: v.to,
    state: v.state,
  }));
  inviteLog.sort((x, y) => x.timestamp - y.timestamp);

  return <Content inviteLog={inviteLog} sendInvite={sendInviteWrapped} />;
};

export const InviteUser: React.FC = () => {
  const sendInvite = sendPartnerInvite;
  return (
    <>
      <Helmet>
        <title>Users - Invite users</title>
      </Helmet>
      <UsersMenu />
      <ContentWrapped sendInvite={sendInvite} />
    </>
  );
};

export default InviteUser;
